/**
 * The ground a spell covers, by its shape and size.
 *
 * Shared, so the tools can draw the shape somebody has chosen and the world
 * can land the spell on exactly those tiles.
 */

import type { TilePos } from './tiles';

export type AreaShape = 'square' | 'plus' | 'diamond' | 'circle' | 'line';

/**
 * Every tile covered, landing at a tile, cast from another.
 *
 * A size of nought is the tile landed on alone, whatever the shape. A line is
 * every tile from the caster to where it lands, not counting the caster's own,
 * and carries on past it by its size.
 */
export function spellArea(shape: AreaShape, size: number, at: TilePos, from: TilePos): TilePos[] {
  const tiles: TilePos[] = [];
  const reach = Math.max(0, Math.min(10, Math.round(size)));

  if (shape === 'line') {
    const dx = at.tx - from.tx;
    const dz = at.tz - from.tz;
    const steps = Math.max(Math.abs(dx), Math.abs(dz));
    if (steps === 0) return [{ ...at }];
    const total = steps + reach;
    for (let step = 1; step <= total; step++) {
      tiles.push({
        tx: from.tx + Math.round((dx * step) / steps),
        tz: from.tz + Math.round((dz * step) / steps),
        plane: at.plane,
      });
    }
    return tiles;
  }

  for (let dz = -reach; dz <= reach; dz++) {
    for (let dx = -reach; dx <= reach; dx++) {
      const covered =
        shape === 'square'
          ? true
          : shape === 'plus'
            ? dx === 0 || dz === 0
            : shape === 'diamond'
              ? Math.abs(dx) + Math.abs(dz) <= reach
              : dx * dx + dz * dz <= reach * reach + reach;
      if (covered) tiles.push({ tx: at.tx + dx, tz: at.tz + dz, plane: at.plane });
    }
  }
  return tiles;
}
